import {
	Autocomplete,
	Box,
	CircularProgress,
	IconButton,
	Link,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import { zoullooColors } from "../../../../../theme";
import { Check, Close, Delete, Edit } from "@mui/icons-material";
import {
	FreelancerOnboardingLanguageViewModel,
	FreelancerOnboardingMySkillsStepViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMySkillsStepViewModel";
import { LanguageProficiency } from "../../../../../models/LanguageProficiency";
import { Language } from "../../../../../models/Language";
import { SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageProficiency as LanguageProficiencyEnum } from "../../../../../models/enums/LanguageProficiency";
import {
	useLanguageProficiencies,
	useLanguages
} from "../../../../../hooks/useReferential";
import { FormikProps } from "formik";

const FreelancerOnboardingMySkillsLanguagesBloc = ({
	formik
}: {
	formik: FormikProps<FreelancerOnboardingMySkillsStepViewModel>;
}) => {
	const { data: languages, isLoading: isLanguagesLoading } = useLanguages();
	const { data: languageProficiencies, isLoading: isLanguageProficienciesLoading } =
		useLanguageProficiencies();
	const { t } = useTranslation();
	const [isAddingLanguage, setIsAddingLanguage] = useState(false);
	const [editingLanguageRowIndex, setEditingLanguageRowIndex] = useState(-1);

	const [addedLanguage, setAddedLanguage] = useState<Language | null>(null);
	const [addedLanguageProficiency, setAddedLanguageProficiency] =
		useState<LanguageProficiency | null>(null);
	const [isAddedLanguageTouched, setIsAddedLanguageTouched] = useState<boolean>(false);
	const [isAddedLanguageProficiencyTouched, setIsAddedLanguageProficiencyTouched] =
		useState<boolean>(false);
	const [updatedLanguage, setUpdatedLanguage] = useState<Language | null>(null);
	const [updatedLanguageProficiency, setUpdatedLanguageProficiency] =
		useState<LanguageProficiency | null>(null);

	const handleAddLanguage = () => {
		setIsAddingLanguage(true);
		setIsAddedLanguageTouched(false);
		setIsAddedLanguageProficiencyTouched(false);
	};

	const handleConfirmAddLanguage = () => {
		formik.setFieldValue("languageViewModels", [
			...formik.values.languageViewModels,
			{
				language: addedLanguage,
				languageProficiency: toLanguageProficiencyEnum(addedLanguageProficiency)
			} as FreelancerOnboardingLanguageViewModel
		]);

		setAddedLanguage(null);
		setAddedLanguageProficiency(null);
		setIsAddingLanguage(false);
	};

	const handleCancelAddLanguage = () => {
		setAddedLanguage(null);
		setAddedLanguageProficiency(null);
		setIsAddingLanguage(false);
	};

	const handleEditLanguage = (rowIndex: number) => {
		setUpdatedLanguage(formik.values.languageViewModels[rowIndex].language);
		setUpdatedLanguageProficiency({
			id: parseInt(formik.values.languageViewModels[rowIndex].languageProficiency),
			label: formik.values.languageViewModels[rowIndex].languageProficiency
		});
		setEditingLanguageRowIndex(rowIndex);
	};

	const handleConfirmEdit = (rowIndex: number) => {
		formik.setFieldValue(`languageViewModels.${rowIndex}`, {
			id: formik.values.languageViewModels[rowIndex].id,
			language: updatedLanguage,
			languageProficiency: toLanguageProficiencyEnum(updatedLanguageProficiency)
		} as FreelancerOnboardingLanguageViewModel);
		setEditingLanguageRowIndex(-1);
	};

	const handleCancelEditLanguage = () => {
		setUpdatedLanguage(null);
		setUpdatedLanguageProficiency(null);
		setEditingLanguageRowIndex(-1);
		setIsAddedLanguageTouched(false);
		setIsAddedLanguageProficiencyTouched(false);
	};

	const handleDeleteLanguage = (rowIndex: number) => {
		formik.setFieldValue(
			"languageViewModels",
			formik.values.languageViewModels.filter((_, i) => i !== rowIndex)
		);
	};

	const toLanguageProficiencyEnum = (languageProficiency: LanguageProficiency | null) => {
		switch (languageProficiency?.label) {
			case LanguageProficiencyEnum.Native:
				return LanguageProficiencyEnum.Native;
			case LanguageProficiencyEnum.Fluent:
				return LanguageProficiencyEnum.Fluent;
			case LanguageProficiencyEnum.Operational:
				return LanguageProficiencyEnum.Operational;
			default:
				return null;
		}
	};

	return (
		<Box sx={{ mb: 4 }}>
			<Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
				{t("FreelancerOnboardingMySkills.languages.title")}
			</Typography>
			<Typography variant="body2" gutterBottom>
				{t("FreelancerOnboardingMySkills.languages.description")}
				<Tooltip
					title={<Typography variant="body2">{t("FreelancerOnboardingMySkills.languages.operationalTooltip")}</Typography>}
					arrow
					slotProps={{
						tooltip: {
							sx: { maxWidth: "none !important" }
						}
					}}
				>
					<HelpOutlineSharpIcon
						fontSize="small"
						sx={{ ml: 0.5, color: zoullooColors.grey[400], cursor: "help", verticalAlign: "middle" }}
					/>
				</Tooltip>
			</Typography>

			<TableContainer component={Paper} elevation={1} sx={{ width: "70%" }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: "330px", fontWeight: 600 }}>
								{t("FreelancerOnboardingMySkills.languages.tableHeaders.language")}
							</TableCell>
							<TableCell sx={{ width: "330px", fontWeight: 600 }}>
								{t("FreelancerOnboardingMySkills.languages.tableHeaders.level")}
							</TableCell>
							<TableCell align="right" sx={{ width: "200px", fontWeight: 600 }}>
								<Link
									href="#"
									onClick={e => {
										e.preventDefault();
										handleAddLanguage();
									}}
								>
									{t("FreelancerOnboardingMySkills.languages.addLanguage")}
								</Link>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{formik.values.languageViewModels.map((languageViewModel, index) => (
							<TableRow
								key={`${languageViewModel.language.id}_${languageViewModel.languageProficiency}`}
							>
								{editingLanguageRowIndex === index ? (
									// Edit mode
									<>
										{[
											{
												getOptionLabel: (e: Language) => t(`Languages.${e.label}`),
												options: (languages ?? []).filter(
													x =>
														!formik.values.languageViewModels.some(
															y =>
																y.language.id === x.id &&
																y.language.id !== languageViewModel.language.id
														)
												),
												value: updatedLanguage,
												onChange: (
													_: SyntheticEvent<Element, Event>,
													value: Language | null
												) => {
													setUpdatedLanguage(value);
												},
												onBlur: () => {
													formik.setFieldTouched(
														`languageViewModels.${index}.language.id`,
														true
													);
												},
												name: `languageViewModels.${index}.language`,
												placeholder: t(
													"FreelancerOnboardingMySkills.languages.selectLanguage"
												),
												error:
													formik.touched.languageViewModels?.[index]?.language?.id ===
														true && Boolean(!updatedLanguage),
												helperText: formik.touched.languageViewModels?.[index]?.language
													?.id === true &&
													Boolean(!updatedLanguage) && (
														<Typography
															component={"span"}
															sx={{
																position: "absolute",
																top: " 53px",
																color: `${zoullooColors.error.main} !important`
															}}
														>
															{t(
																"FreelancerOnboardingMySkills.languages.languageRequired"
															)}
														</Typography>
													),
												isLoading: isLanguagesLoading
											},
											{
												getOptionLabel: (e: Language) =>
													t(`LanguageProficiencies.${e.label}`),
												options: languageProficiencies ?? [],
												value: updatedLanguageProficiency,
												onChange: (
													_: SyntheticEvent<Element, Event>,
													value: LanguageProficiency | null
												) => {
													setUpdatedLanguageProficiency(value);
												},
												onBlur: () => {
													formik.setFieldTouched(
														`languageViewModels.${index}.languageProficiency`,
														true
													);
												},
												name: "",
												placeholder: t(
													"FreelancerOnboardingMySkills.languages.selectLevel"
												),
												error:
													formik.touched.languageViewModels?.[index]
														?.languageProficiency === true &&
													Boolean(!updatedLanguageProficiency),
												helperText: formik.touched.languageViewModels?.[index]
													?.languageProficiency === true &&
													Boolean(!updatedLanguageProficiency) && (
														<Typography
															component={"span"}
															sx={{
																position: "absolute",
																top: " 53px",
																color: `${zoullooColors.error.main} !important`
															}}
														>
															{t("FreelancerOnboardingMySkills.languages.levelRequired")}
														</Typography>
													),
												isLoading: isLanguageProficienciesLoading
											}
										].map(item => (
											<TableCell sx={{ paddingBottom: "40px" }} key={crypto.randomUUID()}>
												<Autocomplete
													isOptionEqualToValue={(option, value) => option.id === value.id}
													getOptionLabel={item.getOptionLabel}
													options={item.options}
													value={item.value}
													onChange={item.onChange}
													onBlur={item.onBlur}
													renderInput={params => (
														<TextField
															{...params}
															name={item.name}
															placeholder={item.placeholder}
															variant="outlined"
															error={item.error}
															helperText={item.helperText}
															slotProps={{
																input: {
																	...params.InputProps,
																	endAdornment: (
																		<>
																			{item.isLoading && <CircularProgress size={25} />}
																			{params.InputProps?.endAdornment}
																		</>
																	)
																}
															}}
														/>
													)}
													sx={{ minWidth: 150 }}
												/>
											</TableCell>
										))}

										<TableCell align="center" sx={{ paddingBottom: "40px" }}>
											<IconButton sx={{ mr: 2 }} onClick={handleCancelEditLanguage}>
												<Close />
											</IconButton>
											<IconButton
												disabled={
													Boolean(!updatedLanguage) ||
													Boolean(!updatedLanguageProficiency)
												}
												onClick={() => handleConfirmEdit(index)}
											>
												<Check />
											</IconButton>
										</TableCell>
									</>
								) : (
									// Display mode
									<>
										<TableCell>
											{t(`Languages.${languageViewModel.language.label}`)}
										</TableCell>
										<TableCell>
											{t(
												`LanguageProficiencies.${languageViewModel.languageProficiency}`
											)}
										</TableCell>
										<TableCell align="center">
											<IconButton
												sx={{ mr: 3 }}
												size="small"
												onClick={() => handleDeleteLanguage(index)}
											>
												<Delete fontSize="small" />
											</IconButton>
											<IconButton size="small" onClick={() => handleEditLanguage(index)}>
												<Edit fontSize="small" />
											</IconButton>
										</TableCell>
									</>
								)}
							</TableRow>
						))}
						{/* Add new language row */}
						{(isAddingLanguage || formik.values.languageViewModels.length === 0) && (
							<TableRow>
								<TableCell sx={{ paddingBottom: "40px" }}>
									<Autocomplete
										getOptionLabel={e => t(`Languages.${e.label}`)}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										options={(languages ?? []).filter(
											x =>
												!formik.values.languageViewModels.some(
													y => y.language.id === x.id
												)
										)}
										value={addedLanguage}
										onChange={(_, value) => setAddedLanguage(value)}
										onBlur={() => {
											formik.setTouched({
												languageViewModels: formik.values.languageViewModels.map(() => ({
													language: {
														id: true
													},
													languageProficiency: true
												}))
											});
											setIsAddedLanguageTouched(true);
										}}
										renderInput={params => (
											<TextField
												{...params}
												placeholder={t(
													"FreelancerOnboardingMySkills.languages.selectLanguage"
												)}
												variant="outlined"
												error={isAddedLanguageTouched && addedLanguage === null}
												helperText={
													isAddedLanguageTouched &&
													addedLanguage === null && (
														<Typography
															component={"span"}
															sx={{
																position: "absolute",
																top: " 53px",
																color: `${zoullooColors.error.main} !important`
															}}
														>
															{t(
																"FreelancerOnboardingMySkills.languages.languageRequired"
															)}
														</Typography>
													)
												}
												slotProps={{
													input: {
														...params.InputProps,
														endAdornment: (
															<>
																{isLanguagesLoading && <CircularProgress size={25} />}
																{params.InputProps?.endAdornment}
															</>
														)
													}
												}}
											/>
										)}
										sx={{ minWidth: 150 }}
									/>
								</TableCell>
								<TableCell sx={{ paddingBottom: "40px" }}>
									<Autocomplete
										getOptionLabel={e => t(`LanguageProficiencies.${e.label}`)}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										options={languageProficiencies ?? []}
										value={addedLanguageProficiency}
										onChange={(_, value) => setAddedLanguageProficiency(value)}
										onBlur={() => {
											formik.setTouched({
												languageViewModels: formik.values.languageViewModels.map(() => ({
													language: {
														id: true
													},
													languageProficiency: true
												}))
											});
											setIsAddedLanguageProficiencyTouched(true);
										}}
										renderInput={params => (
											<TextField
												{...params}
												placeholder={t(
													"FreelancerOnboardingMySkills.languages.selectLevel"
												)}
												variant="outlined"
												error={
													isAddedLanguageProficiencyTouched &&
													addedLanguageProficiency === null
												}
												helperText={
													isAddedLanguageProficiencyTouched &&
													addedLanguageProficiency === null && (
														<Typography
															component={"span"}
															sx={{
																position: "absolute",
																top: " 53px",
																color: `${zoullooColors.error.main} !important`
															}}
														>
															{t("FreelancerOnboardingMySkills.languages.levelRequired")}
														</Typography>
													)
												}
												slotProps={{
													input: {
														...params.InputProps,
														endAdornment: (
															<>
																{isLanguageProficienciesLoading && (
																	<CircularProgress size={25} />
																)}
																{params.InputProps?.endAdornment}
															</>
														)
													}
												}}
											/>
										)}
										sx={{ minWidth: 150 }}
									/>
								</TableCell>
								<TableCell align="center" sx={{ paddingBottom: "40px" }}>
									<IconButton sx={{ mr: 2 }} onClick={handleCancelAddLanguage}>
										<Close />
									</IconButton>
									<IconButton
										disabled={
											Boolean(!addedLanguage) || Boolean(!addedLanguageProficiency)
										}
										onClick={handleConfirmAddLanguage}
									>
										<Check />
									</IconButton>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<Typography marginTop={2} sx={{ color: `${zoullooColors.error.main} !important` }}>
				{(formik.touched.languageViewModels?.some(x => x.language?.id) ||
					formik.touched.languageViewModels?.some(x => x.languageProficiency) ||
					isAddedLanguageTouched ||
					isAddedLanguageProficiencyTouched) &&
					(formik.errors.languageViewModels as string)}
			</Typography>
		</Box>
	);
};

export default FreelancerOnboardingMySkillsLanguagesBloc;
